import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { captureAPI } from '../services/api';
import { formatDocType } from '../utils/formatDocType';
import { formatAiCostUsd } from '../utils/formatAiCost';
import { useIsAdmin } from '../identity/useIsAdmin';
import './VendorProfilesPage.css';

function StatusBadge({ status }) {
  const map = {
    learning:  { label: 'Learning',  cls: 'vp-badge-learning' },
    confident: { label: 'Confident', cls: 'vp-badge-confident' },
    validated: { label: 'Validated', cls: 'vp-badge-validated' },
    suspended: { label: 'Suspended', cls: 'vp-badge-suspended' },
  };
  const s = map[status] || { label: status, cls: 'vp-badge-learning' };
  return <span className={`vp-badge ${s.cls}`}>{s.label}</span>;
}

/**
 * GET /vendor-profiles computes document_count (documents linked now) and
 * ai_cost (every ledger call carrying the profile) at read time. Both are
 * absent / null when that read failed: shown as a dash, never as 0.
 */
const documentCountText = (profile) =>
  typeof profile.document_count === 'number' ? String(profile.document_count) : '\u2014';
const aiCostCardText = (profile) => (profile.ai_cost ? formatAiCostUsd(profile.ai_cost.total_usd) : '\u2014');
const plural = (n, one) => `${n} ${n === 1 ? one : `${one}s`}`;

/** The detail page's tracked line: total, calls and distinct documents. */
export function aiCostDetailText(aiCost) {
  if (!aiCost) return 'Unavailable';
  return `${formatAiCostUsd(aiCost.total_usd)} \u00b7 ${plural(aiCost.calls, 'call')} \u00b7 ${plural(aiCost.documents, 'document')}`;
}

function ConfidenceBar({ score }) {
  const pct = Math.round((score || 0) * 100);
  const cls = pct >= 60 ? 'vp-bar-high' : pct >= 30 ? 'vp-bar-mid' : 'vp-bar-low';
  return (
    <div className="vp-confidence-bar">
      <div className={`vp-confidence-fill ${cls}`} style={{ width: `${pct}%` }} />
      <span className="vp-confidence-label">{pct}%</span>
    </div>
  );
}

// Per-vendor line items: 'follow' (the default) uses the account setting
// (Capture Settings → Extract line items); 'on' / 'off' override it.
const LINE_ITEMS_MODES = ['follow', 'on', 'off'];

function lineItemsModeOf(profile) {
  return LINE_ITEMS_MODES.includes(profile?.line_items_mode) ? profile.line_items_mode : 'follow';
}

function followLabel(accountOn) {
  if (accountOn === true) return 'Follow account setting (currently On)';
  if (accountOn === false) return 'Follow account setting (currently Off)';
  return 'Follow account setting';
}

/**
 * Three-option control for a vendor's line-item mode. `value` null selects no
 * option (the bulk bar, which sets every vendor at once). `accountOn` is the
 * account setting, or null while unknown.
 */
export function LineItemsModeControl({ name, value, accountOn, onChange, disabled }) {
  const labels = { follow: followLabel(accountOn), on: 'On', off: 'Off' };
  return (
    <div className="vp-mode-group" role="radiogroup" aria-label="Line items">
      {LINE_ITEMS_MODES.map(mode => (
        <label key={mode} className={`vp-mode-option ${value === mode ? 'vp-mode-option-selected' : ''}`}>
          <input
            type="radio"
            name={name}
            value={mode}
            checked={value === mode}
            disabled={disabled}
            onChange={() => onChange(mode)}
          />
          <span>{labels[mode]}</span>
        </label>
      ))}
    </div>
  );
}

function DocTypeBadge({ docType }) {
  return <span className="vp-doc-type-badge">{formatDocType(docType)}</span>;
}

function ProfileCard({ profile, onSelect, selected }) {
  return (
    <div
      className={`vp-card ${selected ? 'vp-card-selected' : ''}`}
      onClick={() => onSelect(profile)}
    >
      <div className="vp-card-header">
        <div className="vp-card-header-left">
          <span className="vp-vendor-name">{profile.vendor_name}</span>
          <div className="vp-card-badges">
            <DocTypeBadge docType={profile.doc_type} />
            {profile.use_ai_always && <span className="vp-pill vp-pill-amber">AI Always</span>}
            {profile.handwriting_flag && !profile.use_ai_always && <span className="vp-pill vp-pill-blue">Handwriting</span>}
            {lineItemsModeOf(profile) !== 'follow' && (
              <span className="vp-pill vp-pill-grey">Line items {lineItemsModeOf(profile) === 'on' ? 'on' : 'off'}</span>
            )}
          </div>
        </div>
        <StatusBadge status={profile.status} />
      </div>
      <ConfidenceBar score={profile.confidence_score} />
      <div className="vp-card-stats">
        <div className="vp-stat">
          <span className="vp-stat-value">{documentCountText(profile)}</span>
          <span className="vp-stat-label">Documents</span>
        </div>
        <div className="vp-stat">
          <span className="vp-stat-value">{profile.confirmed_count || 0}</span>
          <span className="vp-stat-label">Confirmed</span>
        </div>
        <div className="vp-stat">
          <span className="vp-stat-value">{profile.pattern_count || 0}</span>
          <span className="vp-stat-label">Patterns</span>
        </div>
        <div className="vp-stat" title="AI cost tracked for this vendor's documents, re-extracts included">
          <span className="vp-stat-value" data-testid="vp-card-ai-cost">{aiCostCardText(profile)}</span>
          <span className="vp-stat-label">AI cost</span>
        </div>
      </div>
      {profile.extraction_notes && (
        <div className="vp-card-notes-indicator">✏️ Has extraction notes</div>
      )}
    </div>
  );
}

function ProfileDetail({ profileId, accountLineItems, onClose, onPatternCountChange, onProfileChanged, onProfileFieldChanged, onProfileDeleted }) {
  const isAdmin = useIsAdmin();
  const [profile, setProfile] = useState(null);
  // The tenant vendor record this profile's printed name resolves to (GET
  // /vendor-profiles/:id vendor_record), or null.
  const [vendorRecord, setVendorRecord] = useState(null);
  const [patterns, setPatterns] = useState([]);
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [loading, setLoading] = useState(true);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [dangerAction, setDangerAction] = useState(null); // 'reset' | 'delete' | null
  const [dangerBusy, setDangerBusy] = useState(false);
  const [dangerResult, setDangerResult] = useState(null);

  useEffect(() => {
    if (!profileId) return;
    setLoading(true);
    captureAPI.getVendorProfile(profileId)
      .then(res => {
        if (res?.ok) {
          setProfile(res.profile);
          setVendorRecord(res.vendor_record ?? null);
          setPatterns(res.patterns || []);
          setNotes(res.profile?.extraction_notes || '');
        }
      })
      .finally(() => setLoading(false));
  }, [profileId]);

  useEffect(() => {
    setPendingDelete(null);
  }, [profile?.id]);

  const handleSaveNotes = async () => {
    setSaving(true);
    try {
      await captureAPI.updateVendorProfileNotes(profileId, notes);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (e) {
      console.warn('Failed to save notes:', e);
    } finally {
      setSaving(false);
    }
  };

  const handleVendorSettingChange = async (field, value) => {
    try {
      await captureAPI.updateVendorProfile(profileId, { [field]: value });
      setProfile(prev => ({ ...prev, [field]: value }));
      onProfileFieldChanged?.(profileId, { [field]: value });
    } catch (e) {
      console.warn(`Failed to update ${field}:`, e);
    }
  };

  const handleDeletePattern = (profileId, fieldKey) => {
    setPendingDelete(fieldKey);
  };

  const confirmDeletePattern = async (profileId, fieldKey) => {
    try {
      await captureAPI.deleteVendorProfilePattern(profileId, fieldKey);
      // Remove from local state immediately
      setPatterns(prev => {
        const updated = prev.filter(p => p.field_key !== fieldKey);
        // Update parent's profile list with new count
        if (onPatternCountChange) {
          onPatternCountChange(profileId, updated.length);
        }
        return updated;
      });
    } catch (err) {
      alert('Failed to delete pattern: ' + err.message);
    } finally {
      setPendingDelete(null);
    }
  };

  const handleResetProfile = async () => {
    setDangerBusy(true);
    setDangerResult(null);
    try {
      const res = await captureAPI.resetVendorProfile(profileId);
      setDangerResult({
        type: 'reset',
        ok: !!res?.ok,
        documentsDeleted: res?.documentsDeleted ?? 0,
        patternsDeleted: res?.patternsDeleted ?? 0,
      });
      setPatterns([]);
      setProfile(prev => prev ? { ...prev, document_count: 0, confirmed_count: 0, confidence_score: 0, status: 'learning' } : prev);
      if (onProfileChanged) onProfileChanged();
    } catch (err) {
      setDangerResult({ type: 'reset', ok: false, error: err?.response?.data?.error || err?.message || 'Reset failed' });
    } finally {
      setDangerBusy(false);
      setDangerAction(null);
    }
  };

  const handleDeleteProfile = async () => {
    setDangerBusy(true);
    setDangerResult(null);
    try {
      const res = await captureAPI.deleteVendorProfile(profileId);
      if (res?.ok) {
        if (onProfileDeleted) onProfileDeleted(profileId);
        if (onClose) onClose();
      } else {
        setDangerResult({ type: 'delete', ok: false, error: res?.error || 'Delete failed' });
      }
    } catch (err) {
      setDangerResult({ type: 'delete', ok: false, error: err?.response?.data?.error || err?.message || 'Delete failed' });
    } finally {
      setDangerBusy(false);
      setDangerAction(null);
    }
  };

  if (loading) return <div className="vp-detail-loading">Loading profile...</div>;
  if (!profile) return null;

  return (
    <div className="vp-detail">
      <div className="vp-detail-header">
        <div>
          <h2 className="vp-detail-name">{profile.vendor_name}</h2>
          <StatusBadge status={profile.status} />
        </div>
        <button className="vp-detail-close" onClick={onClose}>✕</button>
      </div>

      <div className="vp-detail-meta">
        <div className="vp-meta-row">
          <span className="vp-meta-label">Confidence</span>
          <ConfidenceBar score={profile.confidence_score} />
        </div>
        <div className="vp-meta-row">
          <span className="vp-meta-label">Documents</span>
          <span>{documentCountText(profile)} linked, {profile.confirmed_count || 0} confirmed</span>
        </div>
        <div className="vp-meta-row">
          <span className="vp-meta-label">AI cost (tracked)</span>
          <span
            data-testid="vp-ai-cost-detail"
            title="Every AI call recorded for this vendor's documents, including re-extracts and documents deleted since."
          >
            {aiCostDetailText(profile.ai_cost)}
          </span>
        </div>
        {Number(profile.legacy_ai_cost) > 0 && (
          <div className="vp-meta-row">
            <span className="vp-meta-label">Earlier estimate (extraction only)</span>
            <span
              data-testid="vp-ai-cost-legacy"
              title="The running total kept before tracked AI cost. It counted full AI extraction only and is no longer updated. Not included above."
            >
              {formatAiCostUsd(profile.legacy_ai_cost)}
            </span>
          </div>
        )}
        <div className="vp-meta-row">
          <span className="vp-meta-label">Learned Since</span>
          <span>{new Date(profile.created_at).toLocaleDateString()}</span>
        </div>
        <div className="vp-meta-row" data-testid="vp-vendor-record">
          <span className="vp-meta-label">Vendor record</span>
          {vendorRecord ? (
            <span>
              <Link to={`/vendor-directory?vendor=${vendorRecord.id}`}>
                {vendorRecord.display_name || profile.vendor_name}
              </Link>
              {vendorRecord.vendor_number && <> · No. {vendorRecord.vendor_number}</>}
            </span>
          ) : isAdmin ? (
            <Link to={`/vendor-directory?new=1&printed_name=${encodeURIComponent(profile.vendor_name)}`}>
              Create vendor record
            </Link>
          ) : (
            <span>None</span>
          )}
        </div>
      </div>

      {patterns.length > 0 && (
        <div className="vp-detail-section">
          <h3 className="vp-section-title">Learned Field Patterns ({patterns.length})</h3>
          <table className="vp-patterns-table">
            <thead>
              <tr>
                <th>Field</th>
                <th>Anchor</th>
                <th>Position</th>
                <th>Success</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {patterns.map(p => (
                <tr key={p.field_key}>
                  <td className="vp-field-key">{p.field_key}</td>
                  <td className="vp-anchor">
                    {(p.anchor_candidates || []).slice(0, 2).join(', ') || '—'}
                  </td>
                  <td>{p.value_position || '—'}</td>
                  <td>
                    <span className={`vp-success-rate ${(p.success_rate || 0) >= 0.8 ? 'high' : (p.success_rate || 0) >= 0.5 ? 'mid' : 'low'}`}>
                      {Math.round((p.success_rate || 0) * 100)}%
                    </span>
                  </td>
                  <td>
                    {/* Cluster 2 / A1: removing a learned pattern is an
                        admin-only vendor mutation (server enforces). */}
                    {isAdmin && (pendingDelete === p.field_key ? (
                      <span className="vp-delete-confirm">
                        <button className="vp-delete-confirm-yes" onClick={() => confirmDeletePattern(profileId, p.field_key)}>Delete</button>
                        <button className="vp-delete-confirm-no" onClick={() => setPendingDelete(null)}>Cancel</button>
                      </span>
                    ) : (
                      <button
                        className="vp-pattern-delete-btn"
                        title="Remove this learned pattern"
                        onClick={() => handleDeletePattern(profileId, p.field_key)}
                      >
                        ✕
                      </button>
                    ))}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Cluster 2 / A1 + A9: vendor-profile mutations (settings toggles,
          notes, reset, delete) are admin-only — the server enforces via
          accessPolicy adminOnly on vendor-profiles writes. Non-admins keep the
          read-only profile view above and see a single muted note here. */}
      {!isAdmin && (
        <div className="vp-detail-section">
          <p className="vp-setting-hint" style={{ margin: 0 }}>
            Only tenant admins can change vendor profile settings.
          </p>
        </div>
      )}

      {isAdmin && (<>
      <div className="vp-detail-section">
        <div className="vp-setting-row">
          <span className="vp-setting-label vp-setting-label-static">Extract line items</span>
          <LineItemsModeControl
            name={`line-items-mode-${profileId}`}
            value={lineItemsModeOf(profile)}
            accountOn={accountLineItems}
            onChange={mode => handleVendorSettingChange('line_items_mode', mode)}
          />
          <p className="vp-setting-hint vp-setting-hint-flush">
            Whether the extraction pipeline extracts line item table data from this vendor's documents. Follow uses the account setting in Capture Settings.
          </p>
        </div>

        <div className="vp-setting-row">
          <label className="vp-toggle-label">
            <span className="vp-toggle-text">Always use AI extraction</span>
            <button
              type="button"
              role="switch"
              aria-checked={!!profile.use_ai_always}
              className={`vp-toggle ${profile.use_ai_always ? 'vp-toggle-on' : ''}`}
              onClick={() => handleVendorSettingChange('use_ai_always', !profile.use_ai_always)}
            >
              <span className="vp-toggle-knob" />
            </button>
          </label>
          <p className="vp-setting-hint">
            When enabled, this profile will always be processed using Claude AI regardless of pattern confidence. Recommended for complex or inconsistent document layouts.
          </p>
          {profile.use_ai_always && (
            <p className="vp-setting-notice vp-notice-amber">
              This profile will use Pathway D (AI extraction) for all documents. Standard AI extraction costs apply.
            </p>
          )}
        </div>

        <div className="vp-setting-row">
          <label className="vp-toggle-label">
            <span className="vp-toggle-text">Contains handwriting</span>
            <button
              type="button"
              role="switch"
              aria-checked={!!profile.handwriting_flag}
              className={`vp-toggle ${profile.handwriting_flag ? 'vp-toggle-on' : ''}`}
              onClick={() => handleVendorSettingChange('handwriting_flag', !profile.handwriting_flag)}
            >
              <span className="vp-toggle-knob" />
            </button>
          </label>
          <p className="vp-setting-hint">
            When enabled, Claude AI will always be used to extract data from this vendor's documents. OCR will still run but handwritten content will be interpreted by AI.
          </p>
          {profile.handwriting_flag && (
            <p className="vp-setting-notice vp-notice-blue">
              Handwriting detection is active. AI extraction cost applies to all documents from this vendor.
            </p>
          )}
        </div>
      </div>

      <div className="vp-detail-section">
        <h3 className="vp-section-title">Extraction Notes</h3>
        <p className="vp-notes-hint">
          These notes are sent to Claude on every extraction for this vendor. Describe any quirks or corrections needed.
        </p>
        <textarea
          className="vp-notes-textarea"
          value={notes}
          onChange={e => { if (e.target.value.length <= 2000) setNotes(e.target.value); }}
          placeholder={'Example: "The Item # column contains SKU codes. The Description column has the full product name. Extract both separately."'}
          rows={5}
          maxLength={2000}
        />
        <div className="vp-notes-footer">
          <span className="vp-notes-charcount">{notes.length} / 2000 characters</span>
          <div className="vp-notes-actions">
            <button className={`vp-notes-save-btn ${saved ? 'vp-notes-saved' : ''}`} onClick={handleSaveNotes} disabled={saving}>
              {saving ? 'Saving...' : saved ? 'Saved' : 'Save Notes'}
            </button>
            {notes && (
              <button className="vp-notes-clear-btn" onClick={() => setNotes('')}>Clear</button>
            )}
          </div>
        </div>
      </div>

      <div className="vp-detail-section vp-danger-zone">
        <h3 className="vp-section-title vp-danger-title">Danger Zone</h3>

        <div className="vp-danger-row">
          <div className="vp-danger-info">
            <span className="vp-danger-label">Reset Profile</span>
            <span className="vp-danger-hint">
              Removes all linked documents, OCR data, extracted fields, learned patterns, and observations. Keeps the profile row itself so it can relearn from scratch on the next upload.
            </span>
          </div>
          {dangerAction === 'reset' ? (
            <div className="vp-danger-confirm">
              <span className="vp-danger-confirm-text">Are you sure?</span>
              <button className="vp-danger-btn vp-danger-btn-amber" onClick={handleResetProfile} disabled={dangerBusy}>
                {dangerBusy ? 'Resetting...' : 'Yes, reset'}
              </button>
              <button className="vp-danger-cancel" onClick={() => setDangerAction(null)} disabled={dangerBusy}>Cancel</button>
            </div>
          ) : (
            <button className="vp-danger-btn vp-danger-btn-amber-outline" onClick={() => { setDangerResult(null); setDangerAction('reset'); }}>
              Reset Profile
            </button>
          )}
        </div>

        <div className="vp-danger-row">
          <div className="vp-danger-info">
            <span className="vp-danger-label">Delete Profile</span>
            <span className="vp-danger-hint">
              Permanently removes the profile, all linked documents, OCR data, extracted fields, learned patterns, and storage files. This cannot be undone.
            </span>
          </div>
          {dangerAction === 'delete' ? (
            <div className="vp-danger-confirm">
              <span className="vp-danger-confirm-text">Delete permanently?</span>
              <button className="vp-danger-btn vp-danger-btn-red" onClick={handleDeleteProfile} disabled={dangerBusy}>
                {dangerBusy ? 'Deleting...' : 'Yes, delete'}
              </button>
              <button className="vp-danger-cancel" onClick={() => setDangerAction(null)} disabled={dangerBusy}>Cancel</button>
            </div>
          ) : (
            <button className="vp-danger-btn vp-danger-btn-red-outline" onClick={() => { setDangerResult(null); setDangerAction('delete'); }}>
              Delete Profile
            </button>
          )}
        </div>

        {dangerResult && (
          <div className={`vp-danger-result ${dangerResult.ok ? 'vp-danger-result-ok' : 'vp-danger-result-error'}`}>
            {dangerResult.ok ? (
              dangerResult.type === 'reset' ? (
                <>Reset complete — deleted {dangerResult.documentsDeleted} document{dangerResult.documentsDeleted === 1 ? '' : 's'} and {dangerResult.patternsDeleted} pattern{dangerResult.patternsDeleted === 1 ? '' : 's'}.</>
              ) : (
                <>Profile deleted.</>
              )
            ) : (
              <>{dangerResult.type === 'reset' ? 'Reset' : 'Delete'} failed: {dangerResult.error}</>
            )}
          </div>
        )}
      </div>
      </>)}
    </div>
  );
}

const SORT_OPTIONS = [
  { value: 'name_asc', label: 'Name A-Z' },
  { value: 'name_desc', label: 'Name Z-A' },
  { value: 'confidence_desc', label: 'Confidence (high first)' },
  { value: 'confidence_asc', label: 'Confidence (low first)' },
  { value: 'documents_desc', label: 'Most documents' },
  { value: 'ai_cost_desc', label: 'Highest AI cost' },
  { value: 'last_seen_desc', label: 'Recently seen' },
];

const STATUS_FILTERS = [
  { value: 'all', label: 'All' },
  { value: 'learning', label: 'Learning' },
  { value: 'confident', label: 'Confident' },
  { value: 'validated', label: 'Validated' },
  { value: 'suspended', label: 'Suspended' },
];

export default function VendorProfilesPage() {
  const isAdmin = useIsAdmin();
  const [profiles, setProfiles] = useState([]);
  // Account line-item setting (capture_settings.extract_line_items), shown as
  // "Follow account setting (currently On/Off)"; null while unknown.
  const [accountLineItems, setAccountLineItems] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [error, setError] = useState(null);
  const [bulkUpdating, setBulkUpdating] = useState(false);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortBy, setSortBy] = useState('name_asc');

  const filteredProfiles = useMemo(() => {
    let result = profiles;

    if (search.trim()) {
      const q = search.trim().toLowerCase();
      result = result.filter(p => p.vendor_name.toLowerCase().includes(q));
    }

    if (statusFilter !== 'all') {
      result = result.filter(p => p.status === statusFilter);
    }

    result = [...result].sort((a, b) => {
      switch (sortBy) {
        case 'name_asc': return (a.vendor_name || '').localeCompare(b.vendor_name || '');
        case 'name_desc': return (b.vendor_name || '').localeCompare(a.vendor_name || '');
        case 'confidence_desc': return (b.confidence_score || 0) - (a.confidence_score || 0);
        case 'confidence_asc': return (a.confidence_score || 0) - (b.confidence_score || 0);
        // Computed counts and costs; a profile whose figures could not be read sorts last.
        case 'documents_desc': return (b.document_count ?? -1) - (a.document_count ?? -1);
        case 'ai_cost_desc': return (b.ai_cost?.total_usd ?? -1) - (a.ai_cost?.total_usd ?? -1);
        case 'last_seen_desc': return new Date(b.updated_at || 0) - new Date(a.updated_at || 0);
        default: return 0;
      }
    });

    return result;
  }, [profiles, search, statusFilter, sortBy]);

  const handlePatternCountChange = useCallback((profileId, newCount) => {
    setProfiles(prev => prev.map(p => 
      p.id === profileId ? { ...p, pattern_count: newCount } : p
    ));
  }, []);

  const load = useCallback(() => {
    setLoading(true);
    captureAPI.getVendorProfiles()
      .then(res => {
        if (res?.ok) setProfiles(res.profiles || []);
        else setError('Failed to load vendor profiles');
      })
      .catch(() => setError('Failed to load vendor profiles'))
      .finally(() => setLoading(false));
  }, []);

  const handleProfileFieldChanged = useCallback((profileId, patch) => {
    setProfiles(prev => prev.map(p => (p.id === profileId ? { ...p, ...patch } : p)));
  }, []);

  const handleBulkUpdate = useCallback(async (mode) => {
    const label = { follow: 'Follow account setting', on: 'On', off: 'Off' }[mode];
    const n = profiles.length;
    if (!window.confirm(`Set line items to ${label} for all ${n} vendor${n === 1 ? '' : 's'}?`)) return;
    setBulkUpdating(true);
    try {
      const res = await captureAPI.bulkUpdateVendorProfiles({ line_items_mode: mode });
      if (res?.ok) {
        load(); // Refresh the list
      } else {
        setError('Failed to update vendor profiles');
      }
    } catch (err) {
      setError('Failed to update vendor profiles');
    } finally {
      setBulkUpdating(false);
    }
  }, [load, profiles.length]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    let cancelled = false;
    captureAPI.getCaptureSettings()
      .then(res => {
        const value = res?.settings?.extract_line_items;
        if (!cancelled && typeof value === 'boolean') setAccountLineItems(value);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="vp-page">
      <div className="vp-page-header">
        <h2>Vendor Profiles</h2>
        <p className="vp-page-subtitle">
          What the system has learned about each vendor's document layouts. Each confirmed document improves extraction accuracy.
        </p>
      </div>

      {isAdmin && !loading && profiles.length > 0 && (
        <div className="vp-bulk-actions">
          <span className="vp-bulk-label">Line items for all vendors:</span>
          <LineItemsModeControl
            name="line-items-mode-bulk"
            value={null}
            accountOn={accountLineItems}
            onChange={handleBulkUpdate}
            disabled={bulkUpdating}
          />
          {bulkUpdating && <span className="vp-bulk-label">Updating...</span>}
        </div>
      )}

      {loading && <div className="vp-loading">Loading vendors...</div>}
      {error && <div className="vp-error">{error}</div>}

      {!loading && profiles.length > 0 && (
        <div className="vp-toolbar">
          <input
            type="text"
            className="vp-search"
            placeholder="Search vendors..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          <div className="vp-filter-group">
            {STATUS_FILTERS.map(f => (
              <button
                key={f.value}
                className={`vp-filter-btn ${statusFilter === f.value ? 'vp-filter-active' : ''}`}
                onClick={() => setStatusFilter(f.value)}
              >
                {f.label}
              </button>
            ))}
          </div>
          <select
            className="vp-sort-select"
            value={sortBy}
            onChange={e => setSortBy(e.target.value)}
          >
            {SORT_OPTIONS.map(o => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
          {(search || statusFilter !== 'all') && (
            <span className="vp-result-count">
              {filteredProfiles.length} of {profiles.length} vendor{profiles.length !== 1 ? 's' : ''}
            </span>
          )}
        </div>
      )}

      {!loading && profiles.length === 0 && (
        <div className="vp-empty">
          <p>No vendor profiles yet.</p>
          <p>Upload and process invoices — the system will automatically learn vendor patterns.</p>
        </div>
      )}

      <div className={`vp-layout ${selectedId ? 'vp-layout-split' : ''}`}>
        <div className="vp-grid">
          {filteredProfiles.length === 0 && profiles.length > 0 && (
            <div className="vp-no-results">
              No vendors match your search{statusFilter !== 'all' ? ` and "${statusFilter}" filter` : ''}.
            </div>
          )}
          {filteredProfiles.map(p => (
            <ProfileCard
              key={p.id}
              profile={p}
              selected={selectedId === p.id}
              onSelect={prof => setSelectedId(prof.id === selectedId ? null : prof.id)}
            />
          ))}
        </div>

        {selectedId && (
          <ProfileDetail
            profileId={selectedId}
            accountLineItems={accountLineItems}
            onClose={() => setSelectedId(null)}
            onPatternCountChange={handlePatternCountChange}
            onProfileChanged={load}
            onProfileFieldChanged={handleProfileFieldChanged}
            onProfileDeleted={(deletedId) => {
              setProfiles(prev => prev.filter(p => p.id !== deletedId));
              setSelectedId(null);
            }}
          />
        )}
      </div>
    </div>
  );
}
